<template>
  <a-table
    :dataSource="companies"
    :loading="fetchingCompanies"
    :pagination="{
      current: pagination.page,
      pageSize: pagination.size,
      total: pagination.count
    }"
    :columns="columns"
    :scroll="{ x: 1000 }"
    @change="change"
    :rowKey="(record) => record.id"
  >
    <template #filterDropdown="{ column }">
      <div
        style="padding: 8px; display: flex; flex-direction: column; gap: 8px"
      >
        <InputRut
          v-if="column.key.includes('rut')"
          v-model="filters[column.key]"
          @pressEnter="applyFilter(column.key)"
        />
        <a-input
          v-else
          v-model:value="filters[column.key]"
          :placeholder="`Buscar ${column.title}`"
          @pressEnter="applyFilter(column.key)"
        />
        <div style="display: flex; gap: 8px">
          <a-button
            type="primary"
            size="small"
            block
            @click="applyFilter(column.key)"
          >
            <template #icon><search-outlined /></template>
            Buscar
          </a-button>
          <a-button size="small" block @click="clearFilter(column.key)">
            Limpiar
          </a-button>
        </div>
      </div>
    </template>

    <template #filterIcon="{ column }">
      <search-outlined
        :style="{
          color: filters[column.key] ? '#2521E5' : ''
        }"
      />
    </template>

    <template #actionRender="{ record }">
      <span style="display: flex">
        <a-tooltip color="#05045D" :title="`Ver más`">
          <a-button
            type="link"
            @click="companyRedirect(record)"
            class="edit-holding-text"
          >
            <eye-outlined class="blue-icon" />
          </a-button>
        </a-tooltip>
        <a-tooltip color="#05045D" :title="`Editar`">
          <a-button
            type="link"
            @click="editCompany(record.id)"
            class="edit-holding-text"
          >
            <edit-outlined class="blue-icon" />
          </a-button>
        </a-tooltip>
        <a-tooltip color="#05045D" :title="`Eliminar`">
          <a-button type="link" @click="companyDelete(record)">
            <delete-outlined class="delete-holding-text" />
          </a-button>
        </a-tooltip>
      </span>
    </template>
  </a-table>

  <company-modal
    ref="modalRef"
    @companyCreated="reload"
    @updateCompany="reload"
    @deletedCompany="reload"
  />
</template>

<script setup>
import {
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons-vue';
import { ref, computed, watch, reactive, defineExpose } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import InputRut from '@/bewell-components/form-controls/input-rut.vue';
import {
  COMPANIES_FETCH_COMPANIES,
  COMPANIES_GET_COMPANIES,
  COMPANIES_GET_PAGINATION,
  COMPANIES_GET_LOADING
} from '@/store/types';
import CompanyModal from '@/components/clients/company-modal.vue';

const store = useStore();
const router = useRouter();
const route = useRoute();

const filterMap = {
  'holding.businessName': 'holdingName',
  'holding.rut': 'holdingRut',
  'subHolding.name': 'subholdingName',
  businessName: 'companyName',
  rut: 'companyRut'
};

const filters = reactive({
  'holding.businessName': route.query.holdingName || '',
  'holding.rut': route.query.holdingRut || '',
  'subHolding.name': route.query.subholdingName || '',
  businessName: route.query.companyName || '',
  rut: route.query.companyRut || ''
});

const companies = computed(() => {
  return store.getters[COMPANIES_GET_COMPANIES];
});
const pagination = computed(() => {
  return store.getters[COMPANIES_GET_PAGINATION];
});
const fetchingCompanies = computed(() => {
  return store.getters[COMPANIES_GET_LOADING].fetchCompanies;
});

const columns = [
  {
    width: 100,
    title: 'Id',
    dataIndex: 'id',
    key: 'id'
  },
  {
    title: 'Holding',
    dataIndex: 'holding.businessName',
    key: 'holding.businessName',
    slots: {
      filterDropdown: 'filterDropdown',
      filterIcon: 'filterIcon'
    }
  },
  {
    width: 150,
    title: 'RUT Holding',
    dataIndex: 'holding.rut',
    key: 'holding.rut',
    slots: {
      filterDropdown: 'filterDropdown',
      filterIcon: 'filterIcon'
    }
  },
  {
    title: 'Subholding',
    dataIndex: 'subHolding.name',
    key: 'subHolding.name',
    slots: {
      filterDropdown: 'filterDropdown',
      filterIcon: 'filterIcon'
    }
  },
  {
    title: 'Empresa',
    dataIndex: 'businessName',
    key: 'businessName',
    slots: {
      filterDropdown: 'filterDropdown',
      filterIcon: 'filterIcon'
    }
  },
  {
    width: 150,
    title: 'RUT Empresa',
    dataIndex: 'rut',
    key: 'rut',
    slots: {
      filterDropdown: 'filterDropdown',
      filterIcon: 'filterIcon'
    }
  },
  {
    width: 150,
    fixed: 'right',
    align: 'center',
    title: 'Acción',
    dataIndex: 'id',
    key: 'action',
    slots: {
      customRender: 'actionRender'
    }
  }
];

const syncFiltersFromQuery = (query) => {
  Object.entries(filterMap).forEach(([columnKey, queryParam]) => {
    filters[columnKey] = query[queryParam] || '';
  });
};

const fetchCompanies = (query) => {
  store.dispatch(COMPANIES_FETCH_COMPANIES, {
    page: query.page || 1,
    size: query.size || 10,
    ...query
  });
};

const reload = () => {
  fetchCompanies(route.query);
};

const applyFilter = (columnKey) => {
  const filterValue = filters[columnKey];
  const queryParam = filterMap[columnKey];
  const query = { ...route.query };

  if (filterValue && filterValue.trim()) {
    query[queryParam] = filterValue.trim();
  } else {
    delete query[queryParam];
  }

  query.page = 1;

  router.replace({
    name: 'Cliente',
    hash: '#empresas',
    query
  });
};

const clearFilter = (columnKey) => {
  filters[columnKey] = '';
  const queryParam = filterMap[columnKey];
  const query = { ...route.query };
  delete query[queryParam];
  query.page = 1;

  router.replace({
    name: 'Cliente',
    hash: '#empresas',
    query
  });
};

watch(
  [() => route.query, () => route.hash, () => route.name],
  ([newQuery, hash, name]) => {
    if (name !== 'Cliente' || hash !== '#empresas') return;

    syncFiltersFromQuery(newQuery);
    fetchCompanies(newQuery);
  },
  { immediate: true }
);

const modalRef = ref();

const companyCreation = () => {
  modalRef.value.createCompany();
};

const editCompany = (value) => {
  modalRef.value.editCompany(value);
};

const companyDelete = (value) => {
  modalRef.value.deleteCompany(value);
};

const companyRedirect = (record) => {
  const id = record?.id ?? record?.companyId ?? record?.company_id;
  if (!id) return;
  router.push({ name: 'Grupos', params: { id } });
};

const change = (pagination, filters) => {
  router.replace({
    name: 'Cliente',
    hash: '#empresas',
    query: {
      ...route.query,
      page: pagination.current,
      size: pagination.pageSize,
      ...filters
    }
  });
};

defineExpose({
  companyCreation
});
</script>
